if (!customElements.get('quick-comparison-table')) {
  customElements.define(
    'quick-comparison-table',
    class QuickComparisonTable extends HTMLElement {
      connectedCallback() {
        this.lifecycleController?.abort();
        this.lifecycleController = new AbortController();
        this.addEventListener('shopify:block:select', this.onBlockSelect.bind(this), {
          signal: this.lifecycleController.signal
        });
        if (!this.dataset.url) return;

        if (window.Shopify?.designMode) {
          this.init();
        } else if ('requestIdleCallback' in window) {
          this.idleCallback = window.requestIdleCallback(() => this.init(), { timeout: 1500 });
        } else if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.observer.disconnect();
              this.init();
            }
          }, { rootMargin: '400px' });
          this.observer.observe(this);
        } else {
          this.init();
        }
      }

      disconnectedCallback() {
        this.lifecycleController?.abort();
        this.loadController?.abort();
        this.observer?.disconnect();
        if (this.idleCallback !== undefined) window.cancelIdleCallback?.(this.idleCallback);
        this.loadGeneration = (this.loadGeneration || 0) + 1;
        this.removeAttribute('aria-busy');
      }

      onBlockSelect(event) {
        const row = Array.from(this.querySelectorAll('tr[data-compare-key]')).find(
          (candidate) => candidate.dataset.compareKey === event.detail?.blockId
        );
        row?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      setStatus(message) {
        const status = this.querySelector('[data-comparison-status]');
        if (!status) return;
        status.textContent = message || '';
        status.hidden = !message;
      }

      async init() {
        if (!this.isConnected || !this.dataset.url) return;
        this.loadController?.abort();
        const controller = new AbortController();
        this.loadController = controller;
        const generation = (this.loadGeneration || 0) + 1;
        this.loadGeneration = generation;
        this.setAttribute('aria-busy', 'true');
        this.setStatus('');

        try {
          const url = new URL(this.dataset.url, window.location.origin);
          const limit = Math.min(10, Math.max(1, Number.parseInt(this.dataset.limit, 10) || 8));
          url.searchParams.set('limit', limit);
          const response = await fetch(url.toString(), { signal: controller.signal });
          if (!response.ok) throw new Error('Comparison request failed');
          const responseText = await response.text();
          if (controller.signal.aborted || generation !== this.loadGeneration || !this.isConnected) return;

          const parsed = new DOMParser().parseFromString(responseText, 'text/html');
          const recommendations = Array.from(parsed.querySelectorAll('quick-comparison-table')).find(
            (candidate) => candidate.dataset.sectionId === this.dataset.sectionId
          );
          if (!recommendations) throw new Error('Comparison section unavailable');
          const incomingRows = new Map(
            Array.from(recommendations.querySelectorAll('tr[data-compare-key]')).map(
              (row) => [row.dataset.compareKey, row]
            )
          );
          const rows = Array.from(this.querySelectorAll('tr[data-compare-key]'));
          const firstRow = incomingRows.get(rows[0]?.dataset.compareKey);
          if (rows.length && !firstRow) throw new Error('Comparison rows unavailable');
          const productIds = Array.from(new Set(
            Array.from(firstRow?.querySelectorAll('td[data-product-id]') || [])
              .map((cell) => cell.dataset.productId)
              .filter((id) => id && id !== this.dataset.productId)
          )).slice(0, limit);

          rows.forEach((row) => {
            Array.from(row.querySelectorAll('td[data-product-id]')).forEach((cell) => {
              if (cell.dataset.productId !== this.dataset.productId) cell.remove();
            });
            const incomingRow = incomingRows.get(row.dataset.compareKey);
            const cells = new Map(Array.from(incomingRow?.querySelectorAll('td[data-product-id]') || []).map(
              (cell) => [cell.dataset.productId, cell]
            ));
            productIds.forEach((id) => {
              const cell = cells.get(id) || document.createElement('td');
              if (!cells.has(id)) {
                cell.dataset.compareKey = row.dataset.compareKey;
                cell.dataset.productId = id;
                cell.textContent = this.dataset.unavailableText || '—';
              }
              row.appendChild(cell);
            });
          });

          this.setStatus(productIds.length ? '' : this.dataset.emptyText);
          this.dispatchEvent(new CustomEvent(`quick-comparison-table:${productIds.length ? 'loaded' : 'empty'}`));
        } catch (error) {
          if (controller.signal.aborted || generation !== this.loadGeneration || !this.isConnected) return;
          this.setStatus(this.dataset.errorText);
          this.dispatchEvent(new CustomEvent('quick-comparison-table:error'));
        } finally {
          if (generation === this.loadGeneration) this.removeAttribute('aria-busy');
        }
      }
    }
  );
}
